import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Gift, ShoppingBag, Mail, BarChart3, Plus, Trash2, Dices, Lock, Unlock, Percent, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatError } from "../lib/api";

const TABS = [
  { key: "resumen", label: "Resumen", icon: BarChart3 },
  { key: "sorteos", label: "Sorteos", icon: Gift },
  { key: "productos", label: "Productos", icon: ShoppingBag },
  { key: "ofertas", label: "Ofertas", icon: Percent },
  { key: "pedidos", label: "Pedidos", icon: ShoppingBag },
  { key: "leads", label: "Leads", icon: Mail },
  { key: "socios", label: "Socios", icon: Users },
];

const EMPTY_RAFFLE = { title: "", description: "", prize: "", image: "", type: "free", draw_date: "" };
const EMPTY_PRODUCT = { name: "", description: "", price: "", image: "", category: "experiencia", stock: 100 };
const EMPTY_OFFER = { title: "", description: "", code: "", valid_until: "" };

export default function Admin() {
  const [tab, setTab] = useState("resumen");
  const [stats, setStats] = useState(null);
  const [raffles, setRaffles] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [participants, setParticipants] = useState(null);
  const [raffleForm, setRaffleForm] = useState(EMPTY_RAFFLE);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [offerForm, setOfferForm] = useState(EMPTY_OFFER);
  const [editingProduct, setEditingProduct] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/raffles").then(({ data }) => setRaffles(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
    api.get("/offers").then(({ data }) => setOffers(data)).catch(() => {});
  };

  useEffect(() => {
    loadAll();
  }, []);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      loadAll();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const createRaffle = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/admin/raffles", { ...raffleForm, draw_date: raffleForm.draw_date || null });
      setRaffleForm(EMPTY_RAFFLE);
    }, "Sorteo creado");
  };

  const draw = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/raffles/${id}/draw`);
      toast.success(`Ganador: ${data.winner.name} (${data.winner.email})`);
      loadAll();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const viewParticipants = async (r) => {
    try {
      const { data } = await api.get(`/admin/raffles/${r.id}/participants`);
      setParticipants({ raffle: r, list: data });
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const createProduct = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/admin/products", { ...productForm, price: parseFloat(productForm.price) || 0, stock: parseInt(productForm.stock) || 0 });
      setProductForm(EMPTY_PRODUCT);
    }, "Producto creado");
  };

  const saveProductEdit = () =>
    run(async () => {
      await api.put(`/admin/products/${editingProduct.id}`, {
        name: editingProduct.name,
        description: editingProduct.description,
        price: parseFloat(editingProduct.price) || 0,
        image: editingProduct.image,
        category: editingProduct.category,
        stock: parseInt(editingProduct.stock) || 0,
      });
      setEditingProduct(null);
    }, "Producto actualizado");

  const createOffer = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/admin/offers", { ...offerForm, valid_until: offerForm.valid_until || null });
      setOfferForm(EMPTY_OFFER);
    }, "Oferta creada");
  };

  const Stat = ({ label, value, prefix = "" }) => (
    <div className="card-dark p-6" data-testid={`stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</p>
      <p className="font-heading text-4xl text-white mt-2">{prefix}{value ?? "—"}</p>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14" data-testid="admin-page">
      <p className="overline-gold mb-3">Panel de administración</p>
      <h1 className="text-3xl lg:text-5xl text-white font-light tracking-tight">Gestión del <span className="text-gold italic">Club</span></h1>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-white/10 pb-0" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.15em] font-semibold border-b-2 transition-colors duration-200 ${
              tab === t.key ? "border-gold text-gold" : "border-transparent text-white/45 hover:text-white"
            }`}
            data-testid={`admin-tab-${t.key}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "resumen" && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats">
            <Stat label="Socios activos" value={stats.socios_activos} />
            <Stat label="Usuarios" value={stats.usuarios_total} />
            <Stat label="Leads" value={stats.leads} />
            <Stat label="Participaciones" value={stats.participaciones} />
            <Stat label="Sorteos abiertos" value={stats.sorteos_abiertos} />
            <Stat label="Pedidos" value={stats.pedidos} />
            <Stat label="Pedidos pagados" value={stats.pedidos_pagados} />
            <Stat label="Ingresos" value={eur((stats.ingresos_cents || 0) / 100)} />
          </motion.div>
        )}

        {tab === "sorteos" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <form onSubmit={createRaffle} className="card-dark p-6 space-y-3 h-fit" data-testid="admin-raffle-form">
              <p className="text-white font-medium flex items-center gap-2"><Plus size={16} className="text-gold" /> Nuevo sorteo</p>
              <input className="input-dark" placeholder="Título" required value={raffleForm.title} onChange={(e) => setRaffleForm({ ...raffleForm, title: e.target.value })} data-testid="raffle-title" />
              <textarea className="input-dark min-h-[80px]" placeholder="Descripción" value={raffleForm.description} onChange={(e) => setRaffleForm({ ...raffleForm, description: e.target.value })} data-testid="raffle-description" />
              <input className="input-dark" placeholder="Premio" value={raffleForm.prize} onChange={(e) => setRaffleForm({ ...raffleForm, prize: e.target.value })} data-testid="raffle-prize" />
              <input className="input-dark" placeholder="URL de imagen" value={raffleForm.image} onChange={(e) => setRaffleForm({ ...raffleForm, image: e.target.value })} data-testid="raffle-image" />
              <select className="input-dark" value={raffleForm.type} onChange={(e) => setRaffleForm({ ...raffleForm, type: e.target.value })} data-testid="raffle-type">
                <option value="free">Gratuito</option>
                <option value="exclusive">Exclusivo (socios)</option>
              </select>
              <input className="input-dark" type="date" value={raffleForm.draw_date} onChange={(e) => setRaffleForm({ ...raffleForm, draw_date: e.target.value })} data-testid="raffle-draw-date" />
              <button type="submit" disabled={busy} className="btn-gold-solid w-full disabled:opacity-40" data-testid="raffle-create-submit">Crear Sorteo</button>
            </form>

            <div className="lg:col-span-2 space-y-4">
              {participants && (
                <div className="card-dark p-6 border-gold/30" data-testid="participants-panel">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-white font-medium">Participantes: {participants.raffle.title} ({participants.list.length})</p>
                    <button onClick={() => setParticipants(null)} className="text-white/40 hover:text-gold text-xs uppercase tracking-widest" data-testid="participants-close">Cerrar</button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {participants.list.map((p) => (
                      <div key={p.id} className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/60 border-b border-white/5 pb-2" data-testid={`participant-${p.id}`}>
                        <span className="text-white">{p.name}</span><span>{p.email}</span><span>{p.phone}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {raffles.map((r) => (
                <div key={r.id} className="card-dark p-5" data-testid={`admin-raffle-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-white font-medium">{r.title}</p>
                      <p className="text-white/40 text-xs mt-1 uppercase tracking-[0.12em]">
                        {r.type === "exclusive" ? "Exclusivo" : "Gratuito"} · {r.status === "open" ? "Abierto" : "Cerrado"} · {r.participants_count} participantes
                      </p>
                      {r.winner && <p className="text-gold text-xs mt-1" data-testid={`admin-winner-${r.id}`}>Ganador: {r.winner.name} ({r.winner.email})</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => viewParticipants(r)} className="btn-ghost-light !px-4 !py-2 !text-[10px]" data-testid={`raffle-participants-${r.id}`}>
                        <Users size={13} /> Participantes
                      </button>
                      {r.status === "open" ? (
                        <>
                          <button onClick={() => run(() => api.post(`/admin/raffles/${r.id}/close`), "Sorteo cerrado")} disabled={busy}
                            className="btn-ghost-light !px-4 !py-2 !text-[10px]" data-testid={`raffle-close-${r.id}`}>
                            <Lock size={13} /> Cerrar
                          </button>
                          <button onClick={() => draw(r.id)} disabled={busy} className="btn-gold !px-4 !py-2 !text-[10px]" data-testid={`raffle-draw-${r.id}`}>
                            <Dices size={13} /> Elegir Ganador
                          </button>
                        </>
                      ) : (
                        <button onClick={() => run(() => api.post(`/admin/raffles/${r.id}/reopen`), "Sorteo reabierto")} disabled={busy}
                          className="btn-ghost-light !px-4 !py-2 !text-[10px]" data-testid={`raffle-reopen-${r.id}`}>
                          <Unlock size={13} /> Reabrir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "productos" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <form onSubmit={createProduct} className="card-dark p-6 space-y-3 h-fit" data-testid="admin-product-form">
              <p className="text-white font-medium flex items-center gap-2"><Plus size={16} className="text-gold" /> Nuevo producto</p>
              <input className="input-dark" placeholder="Nombre" required value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} data-testid="product-name-input" />
              <textarea className="input-dark min-h-[80px]" placeholder="Descripción" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} data-testid="product-description-input" />
              <input className="input-dark" type="number" step="0.01" placeholder="Precio (€)" required value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} data-testid="product-price-input" />
              <input className="input-dark" placeholder="URL de imagen" value={productForm.image} onChange={(e) => setProductForm({ ...productForm, image: e.target.value })} data-testid="product-image-input" />
              <select className="input-dark" value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} data-testid="product-category-input">
                <option value="experiencia">Experiencia</option>
                <option value="servicio">Servicio</option>
                <option value="equipamiento">Equipamiento</option>
              </select>
              <button type="submit" disabled={busy} className="btn-gold-solid w-full disabled:opacity-40" data-testid="product-create-submit">Crear Producto</button>
            </form>
            <div className="lg:col-span-2 space-y-4">
              {products.map((p) => (
                <div key={p.id} className="card-dark p-5" data-testid={`admin-product-${p.id}`}>
                  {editingProduct?.id === p.id ? (
                    <div className="space-y-3" data-testid={`admin-edit-form-${p.id}`}>
                      <input className="input-dark" value={editingProduct.name}
                        onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })} data-testid="admin-edit-name" />
                      <textarea className="input-dark min-h-[60px]" value={editingProduct.description}
                        onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })} data-testid="admin-edit-description" />
                      <div className="grid grid-cols-2 gap-3">
                        <input className="input-dark" type="number" step="0.01" min="0" value={editingProduct.price}
                          onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })} data-testid="admin-edit-price" />
                        <select className="input-dark" value={editingProduct.category}
                          onChange={(e) => setEditingProduct({ ...editingProduct, category: e.target.value })} data-testid="admin-edit-category">
                          <option value="experiencia">Experiencia</option>
                          <option value="servicio">Servicio</option>
                          <option value="equipamiento">Equipamiento</option>
                        </select>
                      </div>
                      <input className="input-dark" placeholder="URL de imagen" value={editingProduct.image}
                        onChange={(e) => setEditingProduct({ ...editingProduct, image: e.target.value })} data-testid="admin-edit-image" />
                      <div className="flex gap-3">
                        <button onClick={saveProductEdit} disabled={busy} className="btn-gold-solid flex-1 !py-2.5 disabled:opacity-40" data-testid="admin-edit-save">
                          <Save size={14} /> Guardar
                        </button>
                        <button onClick={() => setEditingProduct(null)} className="btn-ghost-light !py-2.5" data-testid="admin-edit-cancel">
                          <X size={14} /> Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <img src={p.image} alt={p.name} className="w-14 h-14 object-cover" />
                        <div>
                          <p className="text-white font-medium text-sm">{p.name}</p>
                          <p className="text-sand text-xs mt-0.5">{eur(p.price)} · {p.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setEditingProduct({ ...p })} disabled={busy}
                          className="text-white/50 hover:text-gold transition-colors" data-testid={`product-edit-${p.id}`} title="Editar">
                          <Pencil size={17} />
                        </button>
                        <button onClick={() => run(() => api.delete(`/admin/products/${p.id}`), "Producto eliminado")} disabled={busy}
                          className="text-white/40 hover:text-red-400 transition-colors" data-testid={`product-delete-${p.id}`} title="Eliminar">
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "ofertas" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <form onSubmit={createOffer} className="card-dark p-6 space-y-3 h-fit" data-testid="admin-offer-form">
              <p className="text-white font-medium flex items-center gap-2"><Plus size={16} className="text-gold" /> Nueva oferta</p>
              <input className="input-dark" placeholder="Título" required value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} data-testid="offer-title" />
              <textarea className="input-dark min-h-[80px]" placeholder="Descripción" value={offerForm.description} onChange={(e) => setOfferForm({ ...offerForm, description: e.target.value })} data-testid="offer-description" />
              <input className="input-dark" placeholder="Código (ej. SOCIO10)" value={offerForm.code} onChange={(e) => setOfferForm({ ...offerForm, code: e.target.value })} data-testid="offer-code" />
              <input className="input-dark" type="date" value={offerForm.valid_until} onChange={(e) => setOfferForm({ ...offerForm, valid_until: e.target.value })} data-testid="offer-valid-until" />
              <button type="submit" disabled={busy} className="btn-gold-solid w-full disabled:opacity-40" data-testid="offer-create-submit">Crear Oferta</button>
            </form>
            <div className="lg:col-span-2 space-y-4">
              {offers.map((o) => (
                <div key={o.id} className="card-dark p-5 flex items-center justify-between gap-4" data-testid={`admin-offer-${o.id}`}>
                  <div>
                    <p className="text-white font-medium text-sm">{o.title} {o.code && <span className="text-gold text-xs ml-2">{o.code}</span>}</p>
                    <p className="text-white/45 text-xs mt-1">{o.description}</p>
                  </div>
                  <button onClick={() => run(() => api.delete(`/admin/offers/${o.id}`), "Oferta eliminada")} disabled={busy}
                    className="text-white/40 hover:text-red-400 transition-colors" data-testid={`offer-delete-${o.id}`}>
                    <Trash2 size={17} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "pedidos" && (
          <div className="card-dark overflow-x-auto" data-testid="admin-orders-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[10px] uppercase tracking-[0.15em] text-white/40">
                  <th className="p-4">Orden</th><th className="p-4">Tipo</th><th className="p-4">Cliente</th><th className="p-4">Total</th><th className="p-4">Método</th><th className="p-4">Estado</th><th className="p-4">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-white/5 text-white/70" data-testid={`order-row-${o.order_number}`}>
                    <td className="p-4 font-mono text-xs">{o.order_number}</td>
                    <td className="p-4">{o.kind === "membership" ? "Membresía" : "Tienda"}</td>
                    <td className="p-4">{o.customer?.name}<br /><span className="text-white/40 text-xs">{o.customer?.email}</span></td>
                    <td className="p-4 text-sand">{eur(o.total_cents / 100)}</td>
                    <td className="p-4">{o.method === "bizum" ? "Bizum" : "Tarjeta"}</td>
                    <td className="p-4">
                      <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 ${o.status === "paid" ? "bg-gold/15 text-gold" : o.status === "failed" ? "bg-red-900/40 text-red-300" : "bg-white/5 text-white/50"}`}>
                        {o.status === "paid" ? "Pagado" : o.status === "failed" ? "Fallido" : "Pendiente"}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-white/40">{o.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && <p className="p-8 text-white/40 text-sm">Sin pedidos todavía.</p>}
          </div>
        )}

        {tab === "leads" && (
          <div className="card-dark overflow-x-auto" data-testid="admin-leads-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[10px] uppercase tracking-[0.15em] text-white/40">
                  <th className="p-4">Nombre</th><th className="p-4">Email</th><th className="p-4">Teléfono</th><th className="p-4">Último sorteo</th><th className="p-4">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 text-white/70" data-testid={`lead-row-${l.id}`}>
                    <td className="p-4 text-white">{l.name}</td>
                    <td className="p-4">{l.email}</td>
                    <td className="p-4">{l.phone}</td>
                    <td className="p-4 text-white/45 text-xs">{l.last_source}</td>
                    <td className="p-4 text-xs text-white/40">{(l.updated_at || l.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {leads.length === 0 && <p className="p-8 text-white/40 text-sm">Sin leads captados todavía.</p>}
          </div>
        )}

        {tab === "socios" && (
          <div className="card-dark overflow-x-auto" data-testid="admin-members-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[10px] uppercase tracking-[0.15em] text-white/40">
                  <th className="p-4">Nombre</th><th className="p-4">Email</th><th className="p-4">Rol</th><th className="p-4">Membresía</th><th className="p-4">Puntos</th><th className="p-4">Alta</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-white/5 text-white/70" data-testid={`member-row-${m.id}`}>
                    <td className="p-4 text-white">{m.name}</td>
                    <td className="p-4">{m.email}</td>
                    <td className="p-4">{m.role === "admin" ? <span className="text-gold">Admin</span> : "Socio"}</td>
                    <td className="p-4">
                      {m.is_member ? <span className="text-gold text-xs">Activa hasta {m.member_until?.slice(0, 10)}</span> : <span className="text-white/35 text-xs">Inactiva</span>}
                    </td>
                    <td className="p-4">{m.points}</td>
                    <td className="p-4 text-xs text-white/40">{m.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
